import ticketsServices from '@/api/ticketsServices';
import type { RootState } from '@/app/store';
import { Ticket } from '@/interfaces/ticket';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

interface FilterParams {
  page: number | undefined;
  size: number | undefined;
  sort: string | undefined;
}

interface MetaData {
  totalPages: number;
  totalElements: number;
}
interface TicketsState {
  tickets: { [key: string]: Ticket[] };
  selectedTicket: Ticket;
  loading: boolean;
  error: string;
  currFilters: string;
  metaData: { [key: string]: MetaData };
  params: FilterParams;
  selectedPicture: { [key: string]: string };
  pictureLoading: boolean;
}

const initialState: TicketsState = {
  tickets: {},
  selectedTicket: {} as Ticket,
  loading: false,
  error: '',
  currFilters: '',
  metaData: {},
  selectedPicture: {},
  pictureLoading: false,
  params: {
    sort: undefined,
    page: undefined,
    size: undefined,
  },
};

export const fetchTickets = createAsyncThunk(
  'tickets/fetch',
  async ({ size, page, sort }: { size: number; page: number; sort: string }, thunkAPI) => {
    try {
      const result = await ticketsServices.fetchTickets({ size, page: page - 1, sort });
      return result;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.data);
    }
  }
);

export const fetchImage = createAsyncThunk('tickets/fetchImage', async ({ id, fileName }: { id: string; fileName: string }, thunkAPI) => {
  try {
    const result = await ticketsServices.fetchTicketFile(id, { fileName });
    return result;
  } catch (error: any) {
    let responseObj = await error;
    const errorMessage = responseObj.message;
    return thunkAPI.rejectWithValue(errorMessage);
  }
});

export const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    refresh: () => initialState,
    setSelectedTicket: (state, action) => {
      state.selectedTicket = action.payload;
    },
    handleParamsChange: (state, action: PayloadAction<{ size: number; page: number; sort: string }>) => {
      const ticketFilters = JSON.stringify(action.payload);
      state.currFilters = ticketFilters;
      state.params.sort = action.payload.sort;
      state.params.page = action.payload.page;
      state.params.size = action.payload.size;
    },
    updateTicket: (state, action: PayloadAction<{ id: string; ticket: Ticket }>) => {
      const { id, ticket } = action.payload;

      Object.keys(state.tickets).forEach(key => {
        state.tickets[key].forEach((item, index) => {
          if (item.id === id) state.tickets[key][index] = ticket;
        });
      });
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchTickets.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchTickets.fulfilled, (state, action) => {
      const { payload: data } = action;
      state.tickets[state.currFilters] = data.data;
      state.metaData[state.currFilters] = { totalPages: data.totalPages, totalElements: data.totalElements };
      state.loading = false;
    });

    builder.addCase(fetchTickets.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(fetchImage.pending, state => {
      state.pictureLoading = true;
    });

    builder.addCase(fetchImage.fulfilled, (state, action) => {
      const {
        payload,
        meta: {
          arg: { id, fileName },
        },
      } = action;
      state.selectedPicture[`${id}-${fileName}`] = payload;
      state.pictureLoading = false;
    });

    builder.addCase(fetchImage.rejected, state => {
      state.pictureLoading = false;
      state.error = 'Error';
    });
  },
});

export const { refresh, setSelectedTicket, handleParamsChange, updateTicket } = ticketsSlice.actions;

export const selectTickets = (state: RootState) => state.tickets;

export default ticketsSlice.reducer;
